import { useEffect, useMemo, useRef } from 'react';
import { generateMapUuid, makeMarkersList } from '@/utils/maps';
import { CLIENT_ENV } from '@/config/configuration';
import { AlgoliaClinicV2, SpecialityChannel } from '@/types/algolia-types';
import { useSearchParams } from 'next/navigation';

type MapProps = {
  items: (Pick<AlgoliaClinicV2, '_geoloc' | 'address' | 'name' | 'license' | 'logoUrl' | 'cap' | 'slug' | 'city' | 'ambulatoryId'> & {
    specializations: { specialityChannels?: SpecialityChannel[] | undefined; label: string };
  })[];

  setUserGeolocation: ({ coordinates, place }: { coordinates: [number, number]; place: string }) => void;
  isUserGeolocationActive: boolean;
};

const InteractiveMap = ({ items, setUserGeolocation, isUserGeolocationActive }: MapProps) => {
  const IFrameRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const sendMessage = (e: { data: { isReady: boolean; userGeoloationCoordinates: [number, number]; useGeolocationPlace: string } }) => {
      if (e.data.userGeoloationCoordinates && e.data.useGeolocationPlace && !isUserGeolocationActive)
        setUserGeolocation({ coordinates: e.data.userGeoloationCoordinates, place: e.data.useGeolocationPlace });
      if (!IFrameRef?.current?.contentWindow || !e.data.isReady || !CLIENT_ENV().NEXT_PUBLIC_MAP_URL) return;
      IFrameRef.current.contentWindow.postMessage(
        {
          mapType: 'interactive',
          items: makeMarkersList(items),
          parentOrigin: window.location.origin,
          isUserGeolocationActive: isUserGeolocationActive,
        },
        CLIENT_ENV().NEXT_PUBLIC_MAP_URL,
      );
    };
    addEventListener('message', sendMessage);
    return () => removeEventListener('message', sendMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });
  const src = useMemo(() => `${CLIENT_ENV().NEXT_PUBLIC_MAP_URL}/?uuid=${generateMapUuid(items)}`, [items]);

  // Hide map if it is rendered inside storyblok
  const params = useSearchParams();
  const isStoryblok = !!params?.get('_storyblok') && !!params?.get('_storyblok_c') && !!params?.get('_storyblok_version');
  if (isStoryblok) return null;

  return CLIENT_ENV().NEXT_PUBLIC_MAP_URL ? (
    <iframe
      ref={IFrameRef}
      /*
        Every time we change src prop, we need to remount the iframe.
        This is because if we don't remount the iframe it’s treated as a content navigation,
        and duplicates the history entry.
      */
      key={src}
      title="Mappa interattiva"
      src={src}
      className="h-full w-full"
      allowFullScreen
      allow="geolocation"
    />
  ) : (
    <div>NEXT_PUBLIC_MAP_URL not defined</div>
  );
};

export default InteractiveMap;
